/**
 * Debug-UI chrome shared by every debug panel.
 *
 * - `makeDraggable` adds pointer dragging to any fixed element. lil-gui 0.21
 *   panels are collapsible (`close()/open()`) and hideable (`hide()/show()`)
 *   but are not draggable, so both the tuning GUI and our perf panel use this.
 * - `debugDock` is the single master button: panels register their root and the
 *   dock shows/hides them all at once via `body.debug-open`, so the whole debug
 *   overlay collapses behind one control.
 *
 * Debug-only: nothing runs until a panel registers.
 */

/** Make `el` draggable by `handle` (defaults to `el`), clamped to the viewport. */
export function makeDraggable(el: HTMLElement, handle: HTMLElement = el): void {
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let originLeft = 0;
  let originTop = 0;
  let moved = false;

  const onDown = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    const rect = el.getBoundingClientRect();
    el.style.position = 'fixed';
    el.style.right = 'auto';
    el.style.bottom = 'auto';
    el.style.margin = '0';
    el.style.left = `${rect.left}px`;
    el.style.top = `${rect.top}px`;
    startX = event.clientX;
    startY = event.clientY;
    originLeft = rect.left;
    originTop = rect.top;
    pointerId = event.pointerId;
    moved = false;
    // Listen on the window so the drag survives a failed pointer capture and
    // keeps tracking while the cursor leaves the handle (standard pattern).
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    event.preventDefault();
    event.stopPropagation();
  };

  const onMove = (event: PointerEvent): void => {
    if (pointerId === null || event.pointerId !== pointerId) return;
    if (Math.abs(event.clientX - startX) > 4 || Math.abs(event.clientY - startY) > 4) {
      moved = true;
    }
    const maxLeft = Math.max(0, window.innerWidth - el.offsetWidth);
    const maxTop = Math.max(0, window.innerHeight - el.offsetHeight);
    const left = Math.min(maxLeft, Math.max(0, originLeft + event.clientX - startX));
    const top = Math.min(maxTop, Math.max(0, originTop + event.clientY - startY));
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  };

  const onUp = (event: PointerEvent): void => {
    if (pointerId === null || event.pointerId !== pointerId) return;
    pointerId = null;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    // A released drag must not also fire the handle's click handler (lil-gui
    // opens/closes its panel on title click). Swallow the click that follows
    // the pointerup when the pointer actually moved; drop it if none arrives.
    if (moved) {
      moved = false;
      const swallowClick = (click: MouseEvent): void => {
        click.stopPropagation();
        click.preventDefault();
      };
      window.addEventListener('click', swallowClick, { capture: true, once: true });
      window.setTimeout(() => window.removeEventListener('click', swallowClick, true), 0);
    }
  };

  handle.addEventListener('pointerdown', onDown);
}

class DebugDock {
  private readonly panels = new Set<HTMLElement>();
  private button: HTMLButtonElement | null = null;
  private open = false;

  /** Register a panel root; it is hidden until the dock is opened. */
  register(el: HTMLElement): void {
    if (this.panels.has(el)) return;
    el.dataset.debugPanel = '';
    this.panels.add(el);
    this.ensureButton();
    this.apply();
  }

  /** Expand/collapse every registered panel. */
  toggle(): void {
    this.open = !this.open;
    this.apply();
  }

  private ensureButton(): void {
    if (this.button || typeof document === 'undefined' || !document.body) return;
    const button = document.createElement('button');
    button.id = 'debug-dock-button';
    button.type = 'button';
    button.textContent = 'DEBUG';
    button.title = 'Show/hide debug panels (`)';
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', () => this.toggle());
    document.body.appendChild(button);
    this.button = button;
  }

  private apply(): void {
    if (typeof document === 'undefined' || !document.body) return;
    document.body.classList.toggle('debug-open', this.open);
    this.button?.setAttribute('aria-expanded', String(this.open));
  }
}

/** Process-wide debug dock; panels register themselves via `register()`. */
export const debugDock = new DebugDock();
